/**
 * Sprint 5B — Klantdetail → Openstaand tab.
 * Toont samenvatting + tabel + knop "Overzicht versturen".
 * Data 100% van backend.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Send, Mail, RefreshCcw, AlertTriangle, TrendingUp, Users,
} from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { TripleStatusBadges } from "./StatusBadges";

function fmt(x, currency = "EUR") {
  const s = Number(x || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${currency} ${s}`;
}

export default function KlantOpenstaandTab({ customerId }) {
  const { user } = useAuth();
  const canSend = ["owner", "admin", "manager"].includes(user?.role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/customers/${customerId}/outstanding`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  if (loading || !data) {
    return (
      <div className="p-8 flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Openstaand laden...
      </div>
    );
  }

  const totals = data.totals || {};
  const hasInvoices = (data.invoices || []).length > 0;

  return (
    <div className="space-y-4" data-testid="klant-openstaand-tab">
      {/* Samenvatting */}
      <div
        className="grid grid-cols-2 lg:grid-cols-4 gap-2"
        data-testid="klant-openstaand-summary"
      >
        <StatCard
          label="Totaal openstaand"
          value={fmt(totals.outstanding_total, totals.currency)}
          icon={TrendingUp} color="text-blue-700"
          testid="klant-openstaand-total"
        />
        <StatCard
          label="Totaal verlopen"
          value={fmt(totals.overdue_total, totals.currency)}
          icon={AlertTriangle}
          color={(totals.overdue_total || 0) > 0 ? "text-red-700" : "text-gray-500"}
          testid="klant-openstaand-overdue"
        />
        <StatCard
          label="Aantal facturen"
          value={String(totals.invoice_count || 0)}
          icon={Users} color="text-slate-700"
          testid="klant-openstaand-count"
        />
        <StatCard
          label="Oudste vervaldatum"
          value={totals.oldest_due_date || "—"}
          icon={AlertTriangle} color="text-amber-700"
          testid="klant-openstaand-oldest"
        />
      </div>

      {/* Actiebar */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-600">
          Alle waarden komen van de backend (Sprint 5A). Frontend rekent
          niets zelf uit.
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={load}
            className="rounded-none h-8"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Ververs
          </Button>
          {canSend && hasInvoices && (
            <Button
              size="sm"
              className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setDialogOpen(true)}
              data-testid="klant-openstaand-send-overview-btn"
            >
              <Mail className="w-3.5 h-3.5 mr-1" /> Openstaand overzicht versturen
            </Button>
          )}
        </div>
      </div>

      {/* Tabel */}
      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table
          className="w-full text-xs"
          data-testid="klant-openstaand-table"
        >
          <thead className="bg-[#0A192F] text-white">
            <tr>
              <th className="p-2 text-left">Factuurnr</th>
              <th className="p-2 text-left">Factuurdatum</th>
              <th className="p-2 text-left">Vervaldatum</th>
              <th className="p-2 text-right">Totaal</th>
              <th className="p-2 text-right">Betaald</th>
              <th className="p-2 text-right">Openstaand</th>
              <th className="p-2 text-center">Dagen verlopen</th>
              <th className="p-2 text-left">Statussen</th>
            </tr>
          </thead>
          <tbody>
            {!hasInvoices ? (
              <tr>
                <td colSpan={8} className="p-6 text-center text-gray-500 italic">
                  Deze klant heeft geen openstaande facturen.
                </td>
              </tr>
            ) : data.invoices.map((r) => (
              <tr
                key={r.id}
                className="border-t border-gray-100"
                data-testid={`klant-openstaand-row-${r.id}`}
              >
                <td className="p-2 font-mono">{r.invoice_number}</td>
                <td className="p-2">{r.invoice_date || "—"}</td>
                <td className="p-2">{r.due_date || "—"}</td>
                <td className="p-2 text-right">{fmt(r.total_incl_vat, r.currency)}</td>
                <td className="p-2 text-right">{fmt(r.total_paid, r.currency)}</td>
                <td className="p-2 text-right font-bold">
                  {fmt(r.amount_due, r.currency)}
                </td>
                <td className={`p-2 text-center ${r.days_overdue > 0 ? "text-red-700 font-bold" : "text-gray-500"}`}>
                  {r.days_overdue > 0 ? r.days_overdue : "—"}
                </td>
                <td className="p-2">
                  <TripleStatusBadges
                    financial_status={r.financial_status}
                    due_status={r.due_status}
                    days_overdue={r.days_overdue}
                    followup_status={r.followup_status}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <KlantOpenstaandEmailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customerId={customerId}
        customerName={data.customer_name}
        customerEmail={data.customer_email}
        totals={totals}
        invoices={data.invoices}
        onSent={() => load()}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="border border-gray-200 bg-white p-3" data-testid={testid}>
      <div className="flex items-center gap-1 text-[11px] text-gray-500 uppercase tracking-wide">
        <Icon className={`w-3 h-3 ${color}`} />
        {label}
      </div>
      <div className={`font-chivo font-bold text-lg mt-1 ${color}`}>
        {value}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Overzicht mail dialog
// ─────────────────────────────────────────────────────────────

function KlantOpenstaandEmailDialog({
  open, onOpenChange, customerId, customerName, customerEmail,
  totals, invoices, onSent,
}) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setTo(customerEmail || "");
      setCc("");
      setSubject("");
    }
  }, [open, customerEmail]);

  const handleSend = async () => {
    setSending(true);
    try {
      const payload = { to: to.trim() || null };
      const ccList = cc.split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean);
      if (ccList.length) payload.cc = ccList;
      if (subject.trim()) payload.subject = subject.trim();
      const r = await adminApi.post(
        `/admin/customers/${customerId}/send-outstanding-overview`,
        payload,
      );
      const status = r.data?.status;
      if (status === "sent") {
        toast.success(`Overzicht verzonden naar ${r.data?.to}`);
      } else if (status === "skipped_no_key") {
        toast.warning("E-mail overgeslagen: Resend API-sleutel ontbreekt.");
      } else {
        toast.warning(`Status: ${status || "onbekend"}`);
      }
      onOpenChange(false);
      if (typeof onSent === "function") onSent(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const cur = totals?.currency || "EUR";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg rounded-none"
        data-testid="klant-openstaand-email-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-chivo text-[#0A192F]">
            <Mail className="w-5 h-5" />
            Openstaand overzicht versturen
          </DialogTitle>
        </DialogHeader>

        <div className="border-l-4 border-blue-500 bg-blue-50 p-2 text-xs text-blue-900">
          De klant ontvangt een tabel-overzicht van alle openstaande
          facturen. Individuele factuur-PDFs worden <strong>niet</strong>
          bijgevoegd — enkel de tabel.
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Ontvanger</Label>
            <Input
              type="email" value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-none mt-1"
              data-testid="klant-openstaand-email-to"
            />
          </div>
          <div>
            <Label className="text-xs">CC (optioneel, komma gescheiden)</Label>
            <Input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              className="rounded-none mt-1"
              placeholder="collega@5999cargo.nl"
              data-testid="klant-openstaand-email-cc"
            />
          </div>
          <div>
            <Label className="text-xs">Onderwerp (optioneel)</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-none mt-1"
              placeholder={`Overzicht openstaande facturen — ${customerName || ""}`}
              data-testid="klant-openstaand-email-subject"
            />
          </div>

          <div className="border border-gray-200 p-2 text-xs">
            <div className="font-bold text-[#0A192F] mb-1">Samenvatting</div>
            <div>Aantal facturen: {totals?.invoice_count || 0}</div>
            <div>Totaal openstaand: {fmt(totals?.outstanding_total, cur)}</div>
            <div>Totaal verlopen: {fmt(totals?.overdue_total, cur)}</div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none"
            disabled={sending}
          >
            Annuleren
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !to.trim()}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="klant-openstaand-email-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Overzicht versturen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
